import initWasm, { writeParquet, Table as WasmTable } from 'parquet-wasm';
import { tableFromArrays, tableToIPC } from 'apache-arrow';
import type { PropertySpec, SimulationRow, AnomalyConfig } from '../types/spec';
// ---------------------------------------------------------------------------
// Pure-math sampling helpers (replaces jstat dependency)
// ---------------------------------------------------------------------------

/** Box-Muller normal sample */
function normalSample(mean: number, std: number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return mean + std * z;
}

function uniformSample(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function logNormalSample(mu: number, sigma: number): number {
  return Math.exp(normalSample(mu, sigma));
}

// ---------------------------------------------------------------------------
// Internal state for stateful anomaly types (drift / stuck)
// ---------------------------------------------------------------------------

interface PropertyState {
  driftOffset: number;
  stuckValue: unknown;
  stuckCaptured: boolean;
}

export type SimulatorState = Record<string, PropertyState>;

export function createSimulatorState(specs: PropertySpec[]): SimulatorState {
  return specs.reduce<SimulatorState>((acc, s) => {
    acc[s.name] = { driftOffset: 0, stuckValue: null, stuckCaptured: false };
    return acc;
  }, {});
}

export function resetSimulatorState(state: SimulatorState): void {
  Object.keys(state).forEach((key) => {
    state[key] = { driftOffset: 0, stuckValue: null, stuckCaptured: false }; // eslint-disable-line no-param-reassign
  });
}

// ---------------------------------------------------------------------------
// Sampling functions
// ---------------------------------------------------------------------------

function sampleNumerical(spec: PropertySpec): number {
  const min = spec.constraints.min ?? 0;
  const max = spec.constraints.max ?? 100;
  const dist = spec.constraints.distribution ?? 'uniform';

  let value: number;

  switch (dist) {
    case 'normal': {
      const mean = (min + max) / 2;
      const stddev = (max - min) / 6;
      value = normalSample(mean, stddev);
      break;
    }
    case 'log_normal': {
      const logMin = Math.log(Math.max(min, 0.001));
      const logMax = Math.log(Math.max(max, 0.01));
      const mu = (logMin + logMax) / 2;
      const sigma = Math.max((logMax - logMin) / 6, 0.01);
      value = logNormalSample(mu, sigma);
      break;
    }
    case 'uniform':
    default:
      value = uniformSample(min, max);
      break;
  }

  if (spec.dataType === 'numerical_discrete') {
    const step = spec.constraints.step ?? 1;
    value = Math.round(value / step) * step;
  }

  return value;
}

function sampleCategorical(spec: PropertySpec): string {
  const values = spec.constraints.lookupValues ?? [];
  if (values.length === 0) return '';
  return values[Math.floor(Math.random() * values.length)];
}

function sampleBoolean(): boolean {
  return Math.random() < 0.5;
}

function sampleDatetime(spec: PropertySpec): string {
  const minDate = spec.constraints.minDate
    ? new Date(spec.constraints.minDate).getTime()
    : new Date('2020-01-01').getTime();
  const maxDate = spec.constraints.maxDate
    ? new Date(spec.constraints.maxDate).getTime()
    : new Date('2026-12-31').getTime();
  const ts = minDate + Math.random() * (maxDate - minDate);
  return new Date(ts).toISOString();
}

function sampleText(spec: PropertySpec): string {
  const maxLen = spec.constraints.maxLength ?? 16;
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const len = Math.max(1, Math.floor(Math.random() * maxLen));
  return Array.from({ length: len }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
}

export function generateSample(spec: PropertySpec): unknown {
  switch (spec.dataType) {
    case 'numerical_continuous':
    case 'numerical_discrete':
      return sampleNumerical(spec);
    case 'categorical':
      return sampleCategorical(spec);
    case 'boolean':
      return sampleBoolean();
    case 'datetime':
      return sampleDatetime(spec);
    case 'text':
      return sampleText(spec);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Anomaly injection
// ---------------------------------------------------------------------------

function injectAnomaly(
  value: unknown,
  spec: PropertySpec,
  anomaly: AnomalyConfig,
  propState: PropertyState
): { value: unknown; isAnomaly: boolean } {
  if (Math.random() * 100 >= anomaly.frequency) {
    if (anomaly.type !== 'stuck') {
      propState.stuckCaptured = false;
    }
    return { value, isAnomaly: false };
  }

  const devPct = anomaly.deviationPercent / 100;

  switch (anomaly.type) {
    case 'spike': {
      if (typeof value === 'number') {
        const range =
          (spec.constraints.max ?? 100) - (spec.constraints.min ?? 0);
        const direction = Math.random() < 0.5 ? 1 : -1;
        return { value: value + direction * range * devPct, isAnomaly: true };
      }
      return { value, isAnomaly: true };
    }

    case 'drift': {
      if (typeof value === 'number') {
        const range =
          (spec.constraints.max ?? 100) - (spec.constraints.min ?? 0);
        propState.driftOffset +=
          range * devPct * 0.1 * (Math.random() < 0.5 ? 1 : -1);
        return { value: value + propState.driftOffset, isAnomaly: true };
      }
      return { value, isAnomaly: true };
    }

    case 'stuck': {
      if (!propState.stuckCaptured) {
        propState.stuckValue = value;
        propState.stuckCaptured = true;
      }
      return { value: propState.stuckValue, isAnomaly: true };
    }

    case 'noise': {
      if (typeof value === 'number') {
        const range =
          (spec.constraints.max ?? 100) - (spec.constraints.min ?? 0);
        const noise = normalSample(0, range * devPct);
        return { value: value + noise, isAnomaly: true };
      }
      return { value, isAnomaly: true };
    }

    default:
      return { value, isAnomaly: false };
  }
}

// ---------------------------------------------------------------------------
// Row generation
// ---------------------------------------------------------------------------

export function generateRow(
  specs: PropertySpec[],
  state: SimulatorState
): SimulationRow {
  const values: Record<string, unknown> = {};
  const anomalyFlags: Record<string, boolean> = {};

  specs
    .filter((spec) => spec.included)
    .forEach((spec) => {
      let value = generateSample(spec);
      let isAnomaly = false;

      const { anomaly } = spec.constraints;
      if (anomaly?.enabled) {
        const propState = state[spec.name] ?? {
          driftOffset: 0,
          stuckValue: null,
          stuckCaptured: false,
        };
        const { value: injectedValue, isAnomaly: injectedFlag } = injectAnomaly(
          value,
          spec,
          anomaly,
          propState
        );
        value = injectedValue;
        isAnomaly = injectedFlag;
      }

      if (typeof value === 'number') {
        value =
          spec.dataType === 'numerical_discrete'
            ? Math.round(value)
            : Math.round(value * 1000) / 1000;
      }

      values[spec.name] = value;
      anomalyFlags[spec.name] = isAnomaly;
    });

  return {
    timestamp: new Date().toISOString(),
    values,
    anomalyFlags,
  };
}

export function generateBatch(
  specs: PropertySpec[],
  count: number,
  state: SimulatorState
): SimulationRow[] {
  return Array.from({ length: count }, () => generateRow(specs, state));
}

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------

export function toCSV(rows: SimulationRow[], specs: PropertySpec[]): string {
  const included = specs.filter((s) => s.included);
  const headers = [
    'timestamp',
    ...included.map((s) => s.name),
    ...included.map((s) => `${s.name}_anomaly`),
  ];

  const csvRows = [headers.join(',')];

  rows.forEach((row) => {
    const line = [
      row.timestamp,
      ...included.map((s) => {
        const v = row.values[s.name];
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return `"${v.replace(/"/g, '""')}"`;
        return String(v);
      }),
      ...included.map((s) => (row.anomalyFlags[s.name] ? 'true' : 'false')),
    ];
    csvRows.push(line.join(','));
  });

  return csvRows.join('\n');
}

export function downloadFile(
  content: string,
  filename: string,
  mimeType: string
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadBinaryFile(
  content: Uint8Array,
  filename: string,
  mimeType: string
): void {
  const blob = new Blob([content.buffer as ArrayBuffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Avro export (Apache Avro Object Container File, no external dependency)
// ---------------------------------------------------------------------------

/** Map simulation DataTypeClass to an Avro primitive type name */
function avroBaseType(dataType: PropertySpec['dataType']): string {
  switch (dataType) {
    case 'numerical_continuous':
      return 'double';
    case 'numerical_discrete':
      return 'int';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

/** Build the Avro record schema for the simulation output */
function buildAvroSchema(included: PropertySpec[]): string {
  const fields: Array<{ name: string; type: unknown; default?: unknown }> = [
    { name: 'timestamp', type: 'string' },
  ];

  included.forEach((spec) => {
    const base = avroBaseType(spec.dataType);
    const nullable = spec.constraints.nullable ?? false;
    fields.push({
      name: spec.name,
      type: nullable ? ['null', base] : base,
      ...(nullable ? { default: null } : {}),
    });
    fields.push({ name: `${spec.name}_anomaly`, type: 'boolean' });
  });

  return JSON.stringify({
    type: 'record',
    name: 'SimulationRow',
    namespace: 'com.pepsico.iot',
    fields,
  });
}

/**
 * Encode simulation rows as an Apache Avro Object Container File (OCF).
 * Uses zigzag/varint encoding for all integer/long values as per the Avro spec.
 */
export function toAvro(
  rows: SimulationRow[],
  specs: PropertySpec[]
): Uint8Array {
  const included = specs.filter((s) => s.included);
  const encoder = new TextEncoder();

  // ---- Primitive encoders ----

  function encodeLong(n: number): number[] {
    // Zigzag-encode: non-negative n -> 2n, negative n -> -2n-1
    let v = n >= 0 ? n * 2 : -n * 2 - 1;
    const out: number[] = [];
    while (v > 127) {
      out.push((v % 128) + 128);
      v = Math.floor(v / 128);
    }
    out.push(v % 128);
    return out;
  }

  function encodeString(s: string): number[] {
    const utf8 = encoder.encode(s);
    return [...encodeLong(utf8.length), ...Array.from(utf8)];
  }

  function encodeBytes(data: Uint8Array): number[] {
    return [...encodeLong(data.length), ...Array.from(data)];
  }

  function encodeBoolean(b: boolean): number[] {
    return [b ? 1 : 0];
  }

  function encodeDouble(d: number): number[] {
    const buf = new ArrayBuffer(8);
    new DataView(buf).setFloat64(0, d, true); // little-endian IEEE 754
    return Array.from(new Uint8Array(buf));
  }

  function encodeFieldValue(value: unknown, spec: PropertySpec): number[] {
    switch (spec.dataType) {
      case 'numerical_continuous':
        return encodeDouble(
          typeof value === 'number' ? value : parseFloat(String(value)) || 0
        );
      case 'numerical_discrete':
        return encodeLong(
          typeof value === 'number'
            ? Math.round(value)
            : parseInt(String(value), 10) || 0
        );
      case 'boolean':
        return encodeBoolean(Boolean(value));
      default:
        return encodeString(String(value ?? ''));
    }
  }

  // ---- Header ----

  const magic = [79, 98, 106, 1]; // "Obj\x01"
  const syncMarker: number[] = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 256)
  );

  const schemaBytes = encoder.encode(buildAvroSchema(included));
  // File metadata is encoded as Avro map<bytes>
  const metaMap: number[] = [
    ...encodeLong(1), // 1 entry in this block
    ...encodeString('avro.schema'), // key
    ...encodeBytes(schemaBytes), // value (raw schema bytes)
    ...encodeLong(0), // end of map
  ];

  const header: number[] = [...magic, ...metaMap, ...syncMarker];

  // ---- Records ----

  const allRecordBytes: number[] = [];

  rows.forEach((row) => {
    allRecordBytes.push(...encodeString(row.timestamp));

    included.forEach((spec) => {
      const value = row.values[spec.name] ?? null;
      const nullable = spec.constraints.nullable ?? false;

      if (nullable) {
        if (value === null || value === undefined) {
          allRecordBytes.push(...encodeLong(0)); // null branch (index 0 of ["null", type])
        } else {
          allRecordBytes.push(...encodeLong(1)); // value branch (index 1)
          allRecordBytes.push(...encodeFieldValue(value, spec));
        }
      } else {
        allRecordBytes.push(...encodeFieldValue(value, spec));
      }

      allRecordBytes.push(
        ...encodeBoolean(row.anomalyFlags[spec.name] ?? false)
      );
    });
  });

  // ---- Data block ----

  const dataBlock: number[] = [
    ...encodeLong(rows.length),
    ...encodeLong(allRecordBytes.length),
    ...allRecordBytes,
    ...syncMarker,
  ];

  return new Uint8Array([...header, ...dataBlock]);
}

// ---------------------------------------------------------------------------
// Parquet export (uses parquet-wasm + apache-arrow)
// ---------------------------------------------------------------------------

let wasmReady: Promise<void> | null = null;

function ensureWasmInit(): Promise<void> {
  if (!wasmReady) {
    wasmReady = initWasm().then(() => undefined);
  }
  return wasmReady;
}

/**
 * Encode simulation rows as a valid Apache Parquet file.
 * Uses parquet-wasm (Rust/WASM) via apache-arrow IPC for reliable output.
 */
export async function toParquet(
  rows: SimulationRow[],
  specs: PropertySpec[]
): Promise<Uint8Array> {
  await ensureWasmInit();

  const included = specs.filter((s) => s.included);

  // Build columnar arrays for Apache Arrow
  const columns: Record<string, unknown[]> = {
    timestamp: rows.map((r) => r.timestamp),
  };

  included.forEach((spec) => {
    columns[spec.name] = rows.map((r) => {
      const v = r.values[spec.name];
      // Arrow needs typed values; coerce based on data type
      switch (spec.dataType) {
        case 'numerical_continuous':
          return typeof v === 'number' ? v : parseFloat(String(v)) || 0;
        case 'numerical_discrete':
          return typeof v === 'number'
            ? Math.round(v)
            : parseInt(String(v), 10) || 0;
        case 'boolean':
          return Boolean(v);
        default:
          return String(v ?? '');
      }
    });
    columns[`${spec.name}_anomaly`] = rows.map(
      (r) => r.anomalyFlags[spec.name] ?? false
    );
  });

  // Create an Arrow table and convert to IPC stream
  const arrowTable = tableFromArrays(columns);
  const ipcStream = tableToIPC(arrowTable, 'stream');

  // Convert Arrow IPC → parquet-wasm Table → Parquet bytes
  const wasmTable = WasmTable.fromIPCStream(ipcStream);
  const parquetBytes = writeParquet(wasmTable);

  return parquetBytes;
}
